import { VerticalBarChart } from "@react-gcc-eds/charts";
import { IDateRange } from "../../../contracts";
import { IPurchaseOrderMetric, IShipmentDeliveredMetric, IWeekMetric } from "../../../store";
import { leadingZero } from "../../../utils";
import { useLocalization } from "../../../utils/localization";
import { usePoStatus } from "../helpers/po-status";
import {
  maxMetricCreationDate,
  minMetricCreationDate,
  NoMetrics,
  noShipmentDeliveredMetric,
  noStatusMetric,
  noWeekMetric
} from "./utils";

const BarsChart = (props: any) => {
  return <VerticalBarChart format=".2s" hideLegend {...props} fill />;
};

const getUniqueMetrics = (metrics: IPurchaseOrderMetric[]) => {
  const map = new Map();

  metrics.forEach(obj => {
    if (map.has(obj.label)) {
      map.get(obj.label).totalCount += obj.totalCount;
    } else {
      map.set(obj.label, { label: obj.label, status: obj.status, totalCount: obj.totalCount });
    }
  });

  return Array.from(map.values());
};

export const WeekBarChart = ({
  metrics,
  loading,
  unit,
  onSelection
}: {
  metrics: IWeekMetric[];
  loading: boolean;
  unit: string;
  onSelection?(data: object): void;
}) => {
  if (loading) {
    return <NoMetrics loading />;
  }
  if (noWeekMetric(metrics)) {
    return <NoMetrics />;
  }

  const barData = {
    common: metrics.map(m => `W${leadingZero(m.week)}`),
    series: [{ name: "", values: metrics.map(m => m.amount) }]
  };

  return (
    <BarsChart
      data={barData}
      unit={unit}
      onClick={(label: string) => {
        if (onSelection) {
          onSelection({
            orderCreationRange: {
              from: minMetricCreationDate(metrics),
              to: maxMetricCreationDate(metrics)
            },
            label: label
          });
        }
      }}
    />
  );
};

export const StatusBarChart = ({
  metrics,
  loading,
  unit,
  datesRange,
  internal,
  onSelection
}: {
  metrics: IPurchaseOrderMetric[];
  loading: boolean;
  unit: string;
  datesRange?: IDateRange;
  internal?: boolean;
  onSelection?(data: object): void;
}) => {
  const { statusTooltip, internalStatusDeliveryTooltip } = useLocalization();
  const { labelToStatus, internalLabelToDeliveryStatus } = usePoStatus();

  const uniqueMetrics = getUniqueMetrics(metrics);

  if (loading) {
    return <NoMetrics loading />;
  }
  if (noStatusMetric(metrics)) {
    return <NoMetrics />;
  }

  const poData = {
    common: uniqueMetrics.map(m => {
      return m.label;
    }),
    descriptions: uniqueMetrics.map(m =>
      internal ? internalStatusDeliveryTooltip(m.status) : statusTooltip(m.status)
    ),
    series: [{ name: "", values: uniqueMetrics.map(m => m.totalCount) }]
  };

  return (
    <BarsChart
      data={poData}
      unit={unit}
      onClick={(label: string) => {
        if (onSelection) {
          onSelection({
            orderCreationRange: datesRange,
            status: internal ? internalLabelToDeliveryStatus(label) : labelToStatus(label)
          });
        }
      }}
    />
  );
};

export const ShipmentDeliveredBarChart = ({
  metrics,
  loading,
  unit
}: {
  metrics: IShipmentDeliveredMetric;
  loading: boolean;
  unit: string;
}) => {
  if (loading) {
    return <NoMetrics loading />;
  }
  if (noShipmentDeliveredMetric(metrics)) {
    return <NoMetrics />;
  }

  const shipmentCommon: string[] = [];
  const shipmentSeries: { name: string; values: number[] }[] = [];

  metrics.dataPoints.map(x => shipmentCommon.push(x.label));
  shipmentSeries.push({ name: "", values: metrics.dataPoints.map(x => x.value) });

  const shipmentData = {
    common: shipmentCommon,
    descriptions: undefined,
    series: shipmentSeries
  };

  return (
    <BarsChart
      data={shipmentData}
      unit={unit}
      onClick={() => console.log("no action is taken on click")}
    />
  );
};
